import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, Download, ClipboardCheck, Facebook, ShoppingBag, ExternalLink, Search, Plus, Trash2, UploadCloud } from "lucide-react";
import { toast } from "sonner";

const extFor = (type = "") => { if (type.includes("word")) return "docx"; if (type.includes("pdf")) return "pdf"; return "file"; };

const CATEGORIES = ["Leaflet", "Guide", "Booklet", "Chart", "Template", "Policy", "Checklist", "Form"];

export default function Resources() {
  const { user } = useAuth();
  const canManage = ["admin", "manager", "trainer"].includes(user?.role);
  const [resources, setResources] = useState([]);
  const [products, setProducts] = useState([]);
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: "Leaflet" });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadResources = useCallback(() => api.get("/resources").then((r) => setResources(r.data)).catch(() => {}), []);
  useEffect(() => {
    loadResources();
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, [loadResources]);

  const categories = useMemo(() => {
    const present = new Set(resources.map((r) => r.category).filter(Boolean));
    const ordered = CATEGORIES.filter((c) => present.has(c));
    const extra = [...present].filter((c) => !CATEGORIES.includes(c)).sort();
    return ["All", ...ordered, ...extra];
  }, [resources]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return resources.filter((r) => {
      const matchCat = activeCategory === "All" || r.category === activeCategory;
      const matchQ = !q || `${r.title} ${r.description || ""}`.toLowerCase().includes(q);
      return matchCat && matchQ;
    });
  }, [resources, query, activeCategory]);

  const submitResource = async () => {
    if (!file) { toast.error("Please choose a file to upload."); return; }
    if (!form.title.trim()) { toast.error("Please enter a title."); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title.trim());
      fd.append("description", form.description.trim());
      fd.append("category", form.category);
      fd.append("file", file);
      await api.post("/resources/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Resource uploaded");
      setAddOpen(false);
      setForm({ title: "", description: "", category: "Leaflet" });
      setFile(null);
      await loadResources();
    } catch (e) {
      toast.error(apiErr(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const deleteResource = async (r) => {
    if (!window.confirm(`Delete "${r.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/resources/${r.id}`);
      toast.success("Resource deleted");
      await loadResources();
    } catch (e) {
      toast.error(apiErr(e?.response?.data?.detail));
    }
  };

  const downloadResource = async (r) => {
    if (!r.template_key && !r.file_url && !r.asset_file && !r.storage_path) { toast.info("This resource will be available to download soon."); return; }
    try {
      const res = await api.get(`/resources/${r.id}/download`, { responseType: "blob" });
      const ext = extFor(res.data.type);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${r.title.replace(/\s+/g, "-")}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  return (
    <div>
      <PageHeader title="Resources library" subtitle="Guides, templates and forms — free for your whole team." />
      <div className="rounded-2xl bg-brand text-white p-6 mb-6 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3"><Facebook className="h-6 w-6 text-coral" /><div><div className="font-semibold">Community Hub</div><div className="text-sm text-white/70">You are invited to join for questions and discussion — open to everyone who has completed any of our training courses.</div></div></div>
        <a href="https://www.facebook.com/groups/27941660172109456" target="_blank" rel="noreferrer"><Button data-testid="resources-facebook" className="rounded-full bg-white text-brand hover:bg-white/90">Join the hub</Button></a>
      </div>

      <div className="flex flex-col lg:flex-row lg:items-center gap-4 mb-6">
        <div className="relative lg:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid="resource-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search leaflets, forms and templates…"
            className="pl-9 rounded-full bg-cream-card border-cream-deep"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`resource-filter-${c.toLowerCase()}`}
              onClick={() => setActiveCategory(c)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors duration-150 ${
                activeCategory === c
                  ? "bg-brand text-white border-brand"
                  : "bg-cream-card text-brand border-cream-deep hover:border-brand"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
        {canManage && (
          <Button data-testid="add-resource-btn" onClick={() => setAddOpen(true)} className="lg:ml-auto rounded-full bg-coral hover:bg-coral/90 text-white">
            <Plus className="h-4 w-4 mr-2" />Add resource
          </Button>
        )}
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
        {filtered.map((r) => (
          <div key={r.id} data-testid={`resource-${r.id}`} className="bg-cream-card border border-cream-deep rounded-2xl p-6 flex flex-col">
            <div className="flex items-start justify-between">
              <div className="h-11 w-11 rounded-xl bg-brand-light grid place-items-center mb-4">
                {r.category === "Checklist" ? <ClipboardCheck className="h-5 w-5 text-brand" /> : <FileText className="h-5 w-5 text-brand" />}
              </div>
              {canManage && r.storage_path && (
                <button data-testid={`resource-delete-${r.id}`} onClick={() => deleteResource(r)} className="text-muted-foreground hover:text-red-600 transition-colors" title="Delete resource">
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
            <Badge variant="outline" className="w-fit">{r.category}</Badge>
            <h3 className="font-heading font-bold text-brand mt-2">{r.title}</h3>
            <p className="text-sm text-muted-foreground mt-2 flex-1">{r.description}</p>
            <Button variant="outline" className="mt-4 rounded-full" data-testid={`resource-download-${r.id}`} onClick={() => downloadResource(r)}><Download className="h-4 w-4 mr-2" />Download</Button>
          </div>
        ))}
        {filtered.length === 0 && (
          <p data-testid="resource-empty" className="text-muted-foreground col-span-full py-8 text-center">No resources match your search.</p>
        )}
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-resource-dialog">
          <DialogHeader><DialogTitle>Add a resource</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="res-title">Title</Label>
              <Input id="res-title" data-testid="res-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Daily Mouth Care Record" />
            </div>
            <div>
              <Label htmlFor="res-category">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger id="res-category" data-testid="res-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="res-desc">Description</Label>
              <Textarea id="res-desc" data-testid="res-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Short description shown on the card" rows={3} />
            </div>
            <div>
              <Label htmlFor="res-file">File (PDF, Word or image)</Label>
              <Input id="res-file" data-testid="res-file-input" type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp" onChange={(e) => setFile(e.target.files?.[0] || null)} />
              {file && <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><UploadCloud className="h-3 w-3" />{file.name}</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button data-testid="res-submit-btn" className="rounded-full bg-brand hover:bg-brand-hover text-white" disabled={saving} onClick={submitResource}>{saving ? "Uploading…" : "Upload resource"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="flex flex-wrap items-end justify-between gap-3 mt-12 mb-5">
        <div>
          <div className="flex items-center gap-2"><ShoppingBag className="h-5 w-5 text-coral" /><h2 className="text-xl font-bold text-brand font-heading">Recommended products & tools</h2></div>
          <p className="text-sm text-muted-foreground mt-1">Mouth-care products and tools recommended by KOHC to support your daily oral care.</p>
        </div>
        <a href="https://www.kohc.co.uk/recommended-products" target="_blank" rel="noreferrer">
          <Button variant="outline" data-testid="products-website-link" className="rounded-full border-brand text-brand hover:bg-brand hover:text-white"><ExternalLink className="h-4 w-4 mr-2" />See the full list on kohc.co.uk</Button>
        </a>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
        {products.map((p) => (
          <div key={p.id} data-testid={`product-${p.id}`} className="group bg-cream-card border border-cream-deep rounded-2xl overflow-hidden flex flex-col transition-transform duration-200 hover:-translate-y-1 hover:shadow-md">
            <div className="h-32 bg-gradient-to-b from-white to-cream grid place-items-center p-4">
              {p.image_url ? <img src={p.image_url} alt={p.name} className="max-h-20 max-w-[65%] object-contain transition-transform duration-200 group-hover:scale-105" /> : <ShoppingBag className="h-8 w-8 text-cream-deep" />}
            </div>
            <div className="p-4 flex flex-col flex-1">
              <Badge variant="outline" className="w-fit text-xs">{p.category}</Badge>
              <h3 className="font-heading font-bold text-brand mt-2 text-sm">{p.name}</h3>
              <p className="text-xs text-muted-foreground mt-1.5 flex-1 line-clamp-3">{p.description}</p>
              {p.link ? (
                <a href={p.link} target="_blank" rel="noreferrer" className="mt-3">
                  <Button size="sm" className="w-full rounded-full bg-brand hover:bg-brand-hover text-white h-9" data-testid={`product-link-${p.id}`}><ExternalLink className="h-4 w-4 mr-2" />View product</Button>
                </a>
              ) : (
                <span className="mt-3 text-xs text-muted-foreground italic">Ask your KOHC trainer where to buy</span>
              )}
            </div>
          </div>
        ))}
        {products.length === 0 && <p className="text-muted-foreground">No products yet.</p>}
      </div>
    </div>
  );
}
